"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Star, ChevronRight, Search, Calendar, Layers, Users } from "lucide-react";
import { useFavorieten } from "@/components/favorieten";
import {
  favorietHref,
  favorietSleutel,
  SOORT_LABEL,
  SOORT_VOLGORDE,
  type Favoriet,
  type Soort,
} from "@/lib/favorieten";
import { datumInJaar } from "@/lib/format";

// Wat de server erbij weet: foto's, klassen, datums, aantallen. Zie
// src/app/api/favorieten/route.ts.
type TeamInfo = {
  soort: "team";
  sectie: "fs" | "gn";
  id: number;
  titel: string;
  ondertitel: string;
  foto: string | null;
  klasse: string | null;
  wedstrijd: { id: number; naam: string; datum: string } | null;
};
type WedstrijdInfo = {
  soort: "wedstrijd";
  id: number;
  naam: string;
  datum: string | null;
  aantalDeelnemers: number;
  aantalKlassen: number;
  geweest: boolean;
};
type KlasseInfo = {
  soort: "klasse";
  sleutel: string;
  naam: string;
  sectieLabel: string;
  aantalRijders: number;
};
type Info = TeamInfo | WedstrijdInfo | KlasseInfo;

function infoSleutel(i: Info): string {
  switch (i.soort) {
    case "team":
      return `team:${i.sectie}-${i.id}`;
    case "wedstrijd":
      return `wedstrijd:${i.id}`;
    case "klasse":
      return `klasse:${i.sleutel}`;
  }
}

// Alles wat je volgt, per soort bij elkaar. Client-side, want de favorieten
// staan op het apparaat zelf en niet in een database - volgen werkt zonder
// account.
//
// De lijst wordt afgeleid uit de opslag en aangevuld met wat de server erbij
// weet. Zo hoeft er niets opgeruimd te worden als je iets ontvolgt, en staan de
// namen die we zelf bewaarden er al vóórdat het antwoord binnen is.
export function FavorietenLijst() {
  const { favorieten, geladen, verwijder } = useFavorieten();
  const [info, setInfo] = useState<Map<string, Info>>(new Map());

  const kenmerk = favorieten.map(favorietSleutel).sort().join("|");

  useEffect(() => {
    // Niets ophalen, en ook niets leegmaken: de kaart wordt op sleutel
    // geraadpleegd, dus wat er van een verwijderde favoriet in blijft staan
    // wordt nooit meer opgezocht. Een setState hier zou alleen een extra
    // renderronde kosten.
    if (!geladen || favorieten.length === 0) return;

    const afbreken = new AbortController();
    fetch("/api/favorieten", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(favorieten),
      signal: afbreken.signal,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d: Info[]) => setInfo(new Map(d.map((i) => [infoSleutel(i), i]))))
      .catch(() => {
        // Netwerkfout: dan tonen we de namen die we zelf bewaard hebben.
      });

    return () => afbreken.abort();
    // Op de sleutels vergelijken, niet op de rij zelf: die krijgt bij elke
    // render een nieuwe verwijzing en zou dit effect eindeloos herhalen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kenmerk, geladen]);

  // Tot de opslag gelezen is niets tonen: anders flitst er even "je volgt nog
  // niets" voorbij bij iemand die er tien heeft.
  if (!geladen) return <div className="h-40" aria-hidden />;

  if (favorieten.length === 0) {
    return (
      <div className="mx-4 flex flex-col items-center gap-3 rounded-xl border border-dashed border-white/15 px-6 py-12 text-center">
        <Star className="size-8 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">
          Je volgt nog niets. Zoek hierboven een team, of tik de ster aan bij een wedstrijd of
          een klasse.
        </p>
        <Link
          href="/kalender"
          className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 font-display text-sm font-black uppercase tracking-wide text-primary-foreground"
        >
          <Calendar className="size-4" aria-hidden />
          Naar de kalender
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {SOORT_VOLGORDE.map((soort) => {
        const groep = favorieten.filter((f) => f.soort === soort);
        if (groep.length === 0) return null;
        return (
          <section key={soort}>
            <GroepKop soort={soort} aantal={groep.length} />
            <ul className="space-y-2 px-4">
              {groep.map((f) => (
                <li key={favorietSleutel(f)}>
                  <Kaart
                    favoriet={f}
                    info={info.get(favorietSleutel(f))}
                    onVerwijder={() => verwijder(f)}
                  />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// De verdeling moet zichtbaar zijn: drie soorten die je op drie manieren volgt,
// met per soort hoeveel het er zijn.
function GroepKop({ soort, aantal }: { soort: Soort; aantal: number }) {
  const Icoon = soort === "team" ? Star : soort === "wedstrijd" ? Calendar : Layers;
  return (
    <div className="mb-3 flex items-center gap-2 px-4">
      <Icoon className="size-4 shrink-0 text-primary" aria-hidden />
      <h2 className="font-display text-sm font-black uppercase tracking-wide text-white">
        {SOORT_LABEL[soort]}
      </h2>
      <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs font-bold tabular-nums text-soft-foreground">
        {aantal}
      </span>
      <span className="ml-1 h-px flex-1 bg-white/10" aria-hidden />
    </div>
  );
}

function Kaart({
  favoriet,
  info,
  onVerwijder,
}: {
  favoriet: Favoriet;
  info: Info | undefined;
  onVerwijder: () => void;
}) {
  return (
    <div className="flex items-stretch gap-2">
      <Link
        href={favorietHref(favoriet)}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-border bg-card p-3 transition-colors active:bg-white/5"
      >
        <Beeld favoriet={favoriet} info={info} />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-white">
            {info && "titel" in info ? info.titel : info && "naam" in info ? info.naam : favoriet.naam}
          </span>
          <Onderregel info={info} />
        </span>
        <ChevronRight className="size-4 shrink-0 text-white/30" aria-hidden />
      </Link>
      <button
        type="button"
        onClick={onVerwijder}
        aria-label={`${favoriet.naam} niet meer volgen`}
        className="shrink-0 rounded-xl px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-white"
      >
        Stop
      </button>
    </div>
  );
}

/** Initialen als er geen foto is; bij Farm Stock is dat de normale toestand. */
function initialenVan(naam: string): string {
  return naam
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// Het beeld links op de kaart.
//
// Liggend en niet vierkant: trekkerfoto's zijn breedbeeld, en een vierkante
// uitsnede snijdt er net de machine uit. Groot genoeg om de trekker te
// herkennen - dat is waarom je hem volgt.
const BEELD = "h-20 w-28 shrink-0 overflow-hidden rounded-lg";

function Beeld({ favoriet, info }: { favoriet: Favoriet; info: Info | undefined }) {
  const [fout, setFout] = useState(false);
  const foto = info?.soort === "team" ? info.foto : null;

  if (foto && !fout) {
    return (
      <span className={`relative ${BEELD} bg-muted`}>
        <Image
          src={foto}
          alt=""
          fill
          sizes="112px"
          onError={() => setFout(true)}
          className="object-cover"
        />
      </span>
    );
  }

  // Zonder foto een vlak met betekenis: een teamnaam krijgt initialen, een
  // wedstrijd en een klasse hun eigen teken. Bij Farm Stock heeft maar een op
  // de zeven machines een foto, dus dit is de normale toestand.
  if (favoriet.soort === "wedstrijd" || favoriet.soort === "klasse") {
    const Icoon = favoriet.soort === "wedstrijd" ? Calendar : Layers;
    return (
      <span
        className={`flex ${BEELD} items-center justify-center border border-primary/40 bg-muted`}
      >
        <Icoon className="size-7 text-primary" aria-hidden />
      </span>
    );
  }

  return (
    <span
      className={`flex ${BEELD} items-center justify-center border border-primary/40 bg-muted font-display text-2xl font-black text-primary`}
    >
      {initialenVan(favoriet.naam) || "?"}
    </span>
  );
}

function Onderregel({ info }: { info: Info | undefined }) {
  if (!info) {
    return <span className="block truncate text-xs text-muted-foreground">Ophalen…</span>;
  }

  if (info.soort === "team") {
    return (
      <>
        {info.ondertitel && (
          <span className="block truncate text-xs text-muted-foreground">{info.ondertitel}</span>
        )}
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
          {info.klasse && <span className="truncate text-muted-foreground">{info.klasse}</span>}
          {info.wedstrijd ? (
            <span className="truncate text-soft-foreground">
              <span className="font-semibold text-primary">
                {datumInJaar(new Date(info.wedstrijd.datum))}
              </span>{" "}
              {info.wedstrijd.naam}
            </span>
          ) : (
            <span className="text-muted-foreground">Nog niet ingeschreven</span>
          )}
        </span>
      </>
    );
  }

  if (info.soort === "wedstrijd") {
    return (
      <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
        {info.datum && (
          <span className={info.geweest ? "text-muted-foreground" : "font-semibold text-primary"}>
            {datumInJaar(new Date(info.datum))}
            {info.geweest ? " · geweest" : ""}
          </span>
        )}
        {info.aantalDeelnemers > 0 && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Users className="size-3" aria-hidden />
            {info.aantalDeelnemers}
          </span>
        )}
        {info.aantalKlassen > 0 && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Layers className="size-3" aria-hidden />
            {info.aantalKlassen}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
      <span className="truncate">{info.sectieLabel}</span>
      {info.aantalRijders > 0 && (
        <span className="inline-flex items-center gap-1">
          <Users className="size-3" aria-hidden />
          {info.aantalRijders} dit seizoen
        </span>
      )}
    </span>
  );
}

/** Voor de lege staat op de zoekpagina hierboven. */
export function ZoekHint() {
  return (
    <p className="px-4 pb-1 text-xs text-muted-foreground">
      <Search className="mr-1 inline size-3" aria-hidden />
      Zoek een team en tik de ster aan om het hier te bewaren.
    </p>
  );
}
